// @flow

import * as React from 'react';

import { ChevronDown, ChevronUp, SlidersHorizontal } from 'lucide-react';

import { connect } from 'react-redux';

import type { To } from 'react-router-dom';
import { compose } from 'redux';

import { Text, View } from 'reshaped';
import { superpathjoin } from 'superpathjoin';

import { serializeQuery } from '@gandi/app-runner';
import { selectCartUUID } from '@gandi/cart';
import { withTranslator, type TranslatorProps } from '@gandi/translate';
import { Button, IconButton } from '@gandi/ui';

import { BulkInput } from 'src/Domain/BulkInput';
import { initialState as initialFilters } from 'src/DomainCreate/modules/reducers/filters';
import { selectFiltersValues } from 'src/DomainCreate/modules/selectors/filters';
import type { Filters } from 'src/DomainCreate/types';
import { withRouter } from 'src/helpers/withRouter';
import type { WithRouterProps, FullState } from 'src/types';

import styles from './DomainInput.scss';

type DomainProcess = 'create' | 'renew' | 'transfer';

type OwnProps = {
  children?: React.Node,
  hideBulk?: boolean,
  hideOptions?: boolean,
  id: string,
  label: string,
  loading: boolean,
  placeholder?: string,
  process: DomainProcess,
  submitSearch: string => mixed,
  title: React.Node,
  value: string,
  isStudioPage?: boolean,
};

type Props = {
  translator: TranslatorProps,
  ...WithRouterProps<
    {
      cartId: string,
    },
    {
      search?: string,
      options?: '0' | '1',
      bulk?: '',
    },
  >,
  cartId: string,
  bulk: boolean,
  options: boolean,
  ...OwnProps,

  filters: Filters,
};

// only use state for vars that cannot be stored in query
type State = {
  value: string,
};

class DomainInput extends React.Component<Props, State> {
  input: {
    current: HTMLInputElement | HTMLTextAreaElement | null,
  };

  constructor(props: Props) {
    super(props);

    this.input = React.createRef();

    this.state = {
      value: props.bulk ? '' : props.value,
    };
  }

  componentDidMount() {
    if (!this.props.value && this.input.current) {
      this.input.current.focus();
    }

    const shouldDisplayOptions =
      this.props.process === 'create' &&
      this.props.options &&
      !this.props.hideOptions;

    if (shouldDisplayOptions !== this.props.options) {
      this.props.setSearchParams(prev => {
        prev.delete('options');

        if (shouldDisplayOptions) {
          prev.set('options', '1');
        }

        return prev;
      });
    }
  }

  /**
   * Add the cartId to the quersytring once created.
   */
  componentDidUpdate(prevProps: Props) {
    // Focus the new input when toggling bulk/single search mode.
    // FIXME: set focus at the end of the input?
    if (this.props.bulk !== prevProps.bulk && this.input.current) {
      this.input.current.focus();
    }

    if (!this.props.bulk) {
      // Push a new entry in the browser history for every new search.
      if (
        this.props.value !== prevProps.value &&
        this.props.value !== prevProps.searchParams.get('search')
      ) {
        this.props.setSearchParams(prev => {
          prev.set('search', this.props.value);

          return prev;
        });
      }

      // if the ?search param is updated but does not match state:
      // - Update the input's value.
      // - Re-launch a search if we have a value to search for.
      // Ex: making two searches in a row, then hitting back button.
      const currentSearch = this.props.searchParams.get('search');
      // TODO: [RR6] I'm pretty sure that searchParams are mutable so prevProps doesn't make sense at all
      // This probably needs to be migrated to FC with useEffect to make sure condition is correctly handled
      const prevSearch = prevProps.searchParams.get('search');

      if (
        currentSearch &&
        currentSearch !== prevSearch &&
        currentSearch !== this.props.value
      ) {
        if (this.input.current) {
          this.input.current.value = currentSearch;
          if (currentSearch) {
            this.handleSubmit();
          }
        }
      }
    }
    // Update cartId param in location as necessary.
    // Cart may have been:
    // - created (adding 1st product, add cartId param to pathname)
    // - deleted (removing last product, remove cartId param from pathname).
    if ((this.props.params.cartId || '') !== this.props.cartId) {
      const newPathname = this.props.params.cartId
        ? this.props.location.pathname.replace(this.props.params.cartId, '')
        : superpathjoin(this.props.location.pathname, this.props.cartId);

      this.props.navigate(
        {
          pathname: newPathname,
          search: this.props.searchParams.toString(),
        },
        { replace: true },
      );
    }
  }

  shouldComponentUpdate(nextProps: Props): boolean {
    const {
      bulk,
      location: { query },
      loading,
      value,
      cartId,
      title,
      options,
      filters,
    } = this.props;

    if (loading !== nextProps.loading) return true;

    if (value !== nextProps.value) return true;

    if (bulk !== nextProps.bulk) return true;

    if (filters !== nextProps.filters) return true;

    if (options !== nextProps.options) return true;

    if (query !== nextProps.location.query) return true;

    if (cartId !== nextProps.cartId) return true;

    if (title !== nextProps.title) return true;

    return false;
  }

  /**
   * - Search domains when the form is submitted.
   * - Push the new search in the browser history.
   */
  handleSubmit: (SyntheticEvent<> | void) => void = evt => {
    if (evt) evt.preventDefault();

    const input = this.input.current;
    if (input) {
      input.blur();
      const value = input.value;

      this.props.submitSearch(value);
    }
  };

  toggleBulk: () => void = () => {
    const { searchParams, setSearchParams } = this.props;
    const currentBulk = searchParams.has('bulk');

    if (!currentBulk) {
      searchParams.set('bulk', '');
      const input = this.input.current;
      const value = input ? input.value : '';

      this.setState({ value });
    } else {
      searchParams.delete('bulk');
    }

    setSearchParams(searchParams);
  };

  renderInput: () => React.MixedElement = () => {
    const domainCreate = this.props.process === 'create';
    const searchTerms = this.state.value.replace(/\s+/g, ' ');

    return (
      <div className={styles.inputContainer}>
        <div className={styles.dropShadow}>
          {/* eslint-disable-next-line @gandi/gandi/no-global-element */}
          <input
            id={this.props.id}
            name={this.props.id}
            ref={this.input}
            className={styles.input}
            type="search"
            defaultValue={searchTerms}
            placeholder={
              this.props.placeholder ||
              this.props.translator.__('Enter one word or a domain name')
            }
            aria-label={this.props.label}
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
          />

          <div className={styles.toggleContainer}>
            {domainCreate &&
              !this.props.hideOptions &&
              this.renderOptionsLink()}
            {this.renderButtonOrSpinner()}
          </div>
        </div>
      </div>
    );
  };

  renderBulkLink: () => React.Node = () => {
    const {
      bulk,
      translator: { __ },
      process,
    } = this.props;

    let title;
    switch (process) {
      case 'renew':
        title = bulk ? __('Single renew') : __('Multiple renew');
        break;
      case 'transfer':
        title = bulk ? __('Single transfer') : __('Multiple transfer');
        break;
      default:
        title = bulk ? __('Single search') : __('Multiple search');
    }

    return (
      <Button
        className={styles.bulkLink}
        kind="link"
        type="button"
        onClick={this.toggleBulk}
      >
        {title}
        {bulk ? <ChevronUp /> : <ChevronDown />}
      </Button>
    );
  };

  renderOptionsLink: () => React.Node = () => {
    const {
      options,
      translator: { __ },
      query,
      filters,
    } = this.props;

    const nextQuery = {
      ...query,
      options: options ? undefined : 1,
    };

    const filtersNumber = Object.keys(initialFilters).filter(
      key => filters[key] !== initialFilters[key],
    ).length;

    const to: To = {
      pathname: this.props.location.pathname,
      search: serializeQuery(nextQuery),
    };

    const title = __('Search options');

    return (
      <Button
        title={title}
        aria-label={title}
        kind="outline"
        to={to}
        className={styles.bulkToggle}
      >
        <SlidersHorizontal />
        {!!filtersNumber && (
          <span className={styles.number}>{filtersNumber}</span>
        )}
      </Button>
    );
  };

  renderButtonOrSpinner: () => React.MixedElement = () => {
    const { translator, loading } = this.props;

    return (
      <IconButton
        loading={loading}
        className={styles.button}
        type="submit"
        icon="magnifying"
      >
        {translator.__('Search')}
      </IconButton>
    );
  };

  render(): React.Node {
    const { isStudioPage, translator } = this.props;

    return (
      <div className={styles.root}>
        {this.props.title}
        {!this.props.bulk && (
          <div>
            {/* eslint-disable-next-line @gandi/gandi/no-global-element */}
            <form
              className={styles.form}
              onSubmit={this.handleSubmit}
              method="get"
            >
              {this.renderInput()}
              {isStudioPage && (
                <View paddingTop={1}>
                  <Text color="neutral-faded">
                    {translator.__(
                      'Gandi Tip: One word domains are often already claimed by someone else.',
                    )}
                  </Text>
                </View>
              )}
            </form>
            {this.props.children || null}
          </div>
        )}
        {!this.props.hideBulk && (
          <div className={styles.bulkContainer}>
            {this.renderBulkLink()}
            {this.props.bulk && (
              <BulkInput
                process={this.props.process}
                submitSearch={this.props.submitSearch}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (
  state: FullState,
  props: WithRouterProps<
    {},
    {
      bulk: string,
      options: any,
    },
  >,
) => ({
  bulk: props.searchParams.has('bulk'),
  options: props.location.query.options === '1',
  cartId: selectCartUUID(state),
  filters: selectFiltersValues(state),
});

export default (compose(
  withTranslator({ propsNamespace: 'translator' }),
  withRouter,
  connect(mapStateToProps),
)(DomainInput): React.ComponentType<OwnProps>);
